import clsx from 'clsx'
import React, { useCallback, useRef } from 'react'
import { useFragment } from 'react-relay'
import { graphql } from 'relay-runtime'

import { Modal } from '../../../../components/Modal/Modal'
import { currencyFormat } from '../../../../display'
import { useGlobalState } from '../../../../state/v2/store'
import { Tab } from '@headlessui/react'
import { Bubble, BubbleVariant } from '../../../../components/Bubble/Bubble'
import { LabelType } from '../ThemeInsight/__generated__/ThemeInsight_InsightV2.graphql'
import { labelTypeColors } from '../ThemeInsight/ThemeInsight'
import { useGlobalSearchQueries } from '../../hooks/useGlobalSearchQueries'
import { AdDetailsModal_PerformanceSummarizedMetaAd$key } from './__generated__/AdDetailsModal_PerformanceSummarizedMetaAd.graphql'
import { CurrencyType } from '../TopPerformingAds/__generated__/TopPerformingAdsQuery.graphql'

interface AdDetailsModalProps {
  close: () => void
  isOpen: boolean
  fragmentKey?: AdDetailsModal_PerformanceSummarizedMetaAd$key
  tagsEnabled: boolean
  purchasesEnabled: boolean
  currencyType: CurrencyType
}

export const adDetailsModalFragment = graphql`
  fragment AdDetailsModal_PerformanceSummarizedMetaAd on PerformanceSummarizedMetaAd {
    id
    name
    amountSpent
    websitePurchases
    websiteAddToCarts
    landingPageViews
    costPerPurchase
    costPerWebsitePurchase
    websitePurchasesPercentile
    websiteAddToCartsPercentile
    costPerPurchasePercentile
    costPerWebsitePurchasePercentile
    purchases
    purchasesPercentile
    landingPageViewsPercentile
    aggregatedLabels
    tags
  }
`
export const AdDetailsModal = ({
  close,
  isOpen,
  fragmentKey,
  tagsEnabled,
  purchasesEnabled,
  currencyType
}: AdDetailsModalProps) => {
  const data = useFragment(adDetailsModalFragment, fragmentKey || null)
  const dataRef = useRef<typeof data>(data)
  const [_, setSearchQueries] = useGlobalSearchQueries()

  const handleLabelClick = useCallback(
    (label: string) => {
      setSearchQueries([
        {
          fieldKey: 'adLabel',
          type: 'includes',
          value: label,
        },
      ])
    },
    [setSearchQueries]
  )

  const handleTagClick = useCallback(
    (tag: string) => {
      setSearchQueries([
        {
          fieldKey: 'tag',
          type: 'includes',
          value: tag,
        },
      ])
    },
    [setSearchQueries]
  )


  if (!data && !dataRef.current) {
    return (
      <Modal close={close} isOpen={isOpen} title="Ad stats">
        Loading
      </Modal>
    )
  }

  if (data) {
    dataRef.current = data
  }

  const ad = dataRef.current!

  // De-duplicate and sort labels
  let aggregatedLabels = new Set<string>();
  if (ad.aggregatedLabels) {
    for(var i = 0; i < ad.aggregatedLabels.length; i++) {
      const l = ad.aggregatedLabels[i]
      if (l) {
        const [labelType, rawLabelName] = l.split(':')
        const regex = /[\[\]"']/g;
        const labelName = rawLabelName.replace(regex, '');
        if (labelName) {
          aggregatedLabels.add(labelTypeColors[labelType as LabelType] + ":" + labelName)
        }
      }
    }
  }
  let aggregatedLabelsArray = Array.from(aggregatedLabels).sort((a, b) => a.localeCompare(b));

  return (
    <Modal close={close} isOpen={isOpen} title="Ad details">
      <div className="flex h-72 w-[400px] flex-col">
        <Tab.Group>
          <div className="border-b-1 flex items-center justify-between">
            <h3
              className="text-trueBlue w-1/2 cursor-context-menu truncate break-words font-medium"
              title={ad.name || ''}
            >
              {ad.name}
            </h3>
            <Tab.List className="flex gap-2">
              <Tab className="ui-selected:bg-trueBlue ui-selected:text-white hover:bg-trueBlue rounded p-1 hover:text-white">
                Labels
              </Tab>
              <Tab className="ui-selected:bg-trueBlue ui-selected:text-white hover:bg-trueBlue rounded p-1 hover:text-white">
                Stats
              </Tab>
              {tagsEnabled && <Tab className="ui-selected:bg-trueBlue ui-selected:text-white hover:bg-trueBlue rounded p-1 hover:text-white">
                Tags
              </Tab>}
            </Tab.List>
          </div>
          <Tab.Panels className="bg-offWhite mt-2 flex flex-1 flex-col overflow-y-auto shadow">
            <Tab.Panel className="flex flex-1 flex-col">
              <div className="p-2">
                <div className="flex items-center gap-2">
                  <div className="bg-darkBlue h-4 w-4 rounded-sm" />
                  <span>Video</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-lightBlue h-4 w-4 rounded-sm" />
                  <span>Image</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-midGray h-4 w-4 rounded-sm" />
                  <span>Text</span>
                </div>
              </div>
              <div className="flex w-full flex-1 flex-wrap items-start gap-2 p-2">
                {aggregatedLabelsArray.map((l) => {
                  const [labelColor, labelName] = l.split(':')
                  return (
                    <Bubble
                      variant={labelColor as BubbleVariant}
                      className="rounded hover:cursor-pointer hover:underline"
                      role="button"
                      onClick={() => handleLabelClick(labelName)}
                      key={l}
                    >
                      <span className="truncate whitespace-nowrap text-sm">
                        <span>{labelName}</span>
                      </span>
                    </Bubble>
                  )
                })}
              </div>
            </Tab.Panel>
            <Tab.Panel className="h-full">
              <ul className="divide-y">
                {purchasesEnabled ? (
                  <AdPercentileStat
                    statValue={(ad.purchases || "0").toLocaleString()}
                    label="Purchases"
                    percentile={ad.purchasesPercentile}
                  />
                ) : (
                  ad.websitePurchases !== null && (
                    <AdPercentileStat
                      statValue={ad.websitePurchases.toLocaleString()}
                      label="Website Purchases"
                      percentile={ad.websitePurchasesPercentile}
                    />
                  )
                )}
                {ad.websiteAddToCarts !== null && (
                  <AdPercentileStat
                    statValue={ad.websiteAddToCarts.toLocaleString()}
                    label="Website Add to Carts"
                    percentile={ad.websiteAddToCartsPercentile}
                  />
                )}
                {ad.landingPageViews !== null && (
                  <AdPercentileStat
                    statValue={ad.landingPageViews.toLocaleString()}
                    label="Landing Page Views"
                    percentile={ad.landingPageViewsPercentile}
                  />
                )}
                {ad.amountSpent !== null && (
                  <AdPercentileStat
                    statValue={currencyFormat(ad.amountSpent, currencyType)}
                    label="Amount Spent"
                  />
                )}
                {purchasesEnabled ? (
                  ad.costPerPurchase !== null && (
                    <AdPercentileStat
                      statValue={currencyFormat(ad.costPerPurchase, currencyType)}
                      label="Cost per Purchase"
                      percentile={ad.costPerPurchasePercentile}
                    />
                  )
                ) : (
                  ad.costPerWebsitePurchase !== null && (
                    <AdPercentileStat
                      statValue={currencyFormat(ad.costPerWebsitePurchase, currencyType)}
                      label="Cost per Website Purchase"
                      percentile={ad.costPerWebsitePurchasePercentile}
                    />
                  )
                )}
              </ul>
            </Tab.Panel>
            {tagsEnabled &&
              <Tab.Panel className="flex flex-col">
                <div className="flex w-full flex-1 flex-wrap items-start gap-2 p-2">
                  {ad.tags?.map((tag) => {
                    return (
                      <Bubble
                        variant="grey"
                        className="rounded hover:cursor-pointer hover:underline"
                        onClick={() => handleTagClick(tag)}
                        role="button"
                        key={tag}
                      >
                        <span className="truncate whitespace-nowrap text-sm">
                          <span>{tag}</span>
                        </span>
                      </Bubble>
                    )
                  })}
                </div>
              </Tab.Panel>}
          </Tab.Panels>
        </Tab.Group>
      </div>
    </Modal>
  )
}

interface AdPercentileStatProps {
  percentile?: number | null
  statValue: string
  label: string
}
const AdPercentileStat = ({
  percentile,
  statValue,
  label,
}: AdPercentileStatProps) => {

  return (
    <li className="flex items-center gap-2 py-2">
      <div
        className={clsx('h-4 w-4 rounded-full', {
          'bg-green-500': percentile === 4,
          'bg-yellow-500': percentile === 2 || percentile === 3,
          'bg-red-500': percentile === 1,
        })}
      />

      <div>
        <span>{label}</span>:{' '}
        <span className="text-gray-600">
          {statValue}
        </span>
      </div>
    </li>
  )
}
